<template>
  <div class="cal-mbar">
    <div class="cal-mbar__track">
      <button v-for="option in options" :key="option.text"
              :class="['cal-mbar__btn', { 'cal-mbar__btn--active': selected === option.text, 'cal-mbar__btn--current': option.currentMonth && selected !== option.text }]"
              @click="onOptionSelected(option)">
        {{ option.text }}
      </button>
      <button v-if="enableCustom"
              :class="['cal-mbar__btn cal-mbar__btn--custom', { 'cal-mbar__btn--active': selected === 'custom' }]"
              @click.stop="toggleCustom">
        <i class="fa-regular fa-calendar-range mr-25" style="font-size:.82rem"/>
        {{ $t('calendarPicker.custom') }}
      </button>
    </div>

    <transition name="cal-mbar-fade">
      <div v-if="selected === 'custom'" class="cal-mbar__range">
        <i class="fa-solid fa-arrow-right-arrow-left mr-50" style="font-size:.7rem;opacity:.5"/>
        {{ customRange.start | moment($t('timeFormat.dateMonth.format')) }}
        <span class="cal-mbar__range-sep">&mdash;</span>
        {{ customRange.end | moment($t('timeFormat.dateMonth.format')) }}
      </div>
    </transition>

    <!-- Custom dropdown panel -->
    <transition name="cal-drop">
      <div v-if="showCustom" class="cal-mdrop" v-click-outside="closeCustom">
        <div class="cal-mdrop__header">
          <i class="fa-regular fa-calendar-days mr-50 cal-mdrop__header-icon"/>
          <span>{{ $t('calendarPicker.custom') }}</span>
          <button class="cal-mdrop__close" @click="closeCustom"><i class="fa-solid fa-xmark"/></button>
        </div>
        <div class="cal-mdrop__body">
          <month-picker-range v-model="pendingRange" @input="onPickerInput"/>
        </div>
        <div class="cal-mdrop__footer">
          <button class="cal-mdrop__apply" @click="applyCustom">
            <i class="fa-solid fa-check mr-50"/>{{ $t('calendarPicker.custom') }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import MonthPickerRange from "@/components/widgets/calendar/monthPickerRange.vue";

export default {
  name: 'CalendarMonthlyBar',
  components: { MonthPickerRange },
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._outsideHandler = (e) => { if (!el.contains(e.target)) binding.value(e) }
        setTimeout(() => document.addEventListener('click', el._outsideHandler), 0)
      },
      unbind(el) { document.removeEventListener('click', el._outsideHandler) },
    }
  },
  data: () => ({
    selected: 'today',
    showCustom: false,
    customRange: { start: null, end: null },
    pendingRange: { start: null, end: null },
  }),
  props: {
    value: { default: null },
    startKey: { default: 'start' },
    endKey: { default: 'end' },
    enableCustom: { default: () => true },
    maxOptionsSize: { default: () => 10 },
    futureOptionsSize: { default: () => 0 },
  },
  created() {
    this.onOptionSelected(this.options.find(o => o.currentMonth))
  },
  computed: {
    options() {
      let max = this.maxOptionsSize
      const m = this.$moment
      const months = []
      for (let i = (this.futureOptionsSize || 0); i > 0; i--) {
        max--
        months.push({ value: { start: m().add(i,'months').startOf('month').format('YYYY-MM-DD'), end: m().add(i,'months').endOf('month').format('YYYY-MM-DD') }, text: m().add(i,'months').format('MMMM YY'), currentMonth: false })
      }
      for (let i = 0; i < 12 && max >= 0; i++) {
        max--
        months.push({ value: { start: m().subtract(i,'months').startOf('month').format('YYYY-MM-DD'), end: m().subtract(i,'months').endOf('month').format('YYYY-MM-DD') }, text: m().subtract(i,'months').format('MMMM YY'), currentMonth: i === 0 })
      }
      return months
    },
  },
  methods: {
    onOptionSelected(option) {
      this.selected = option.text
      this.showCustom = false
      this.customRange = option.value
      this.$emit('input', { [this.startKey]: option.value.start, [this.endKey]: option.value.end })
    },
    toggleCustom() {
      this.pendingRange = { ...this.customRange }
      this.showCustom = !this.showCustom
    },
    closeCustom() { this.showCustom = false },
    onPickerInput(val) { if (val) this.pendingRange = val },
    applyCustom() {
      this.customRange = { ...this.pendingRange }
      this.selected = 'custom'
      this.showCustom = false
      this.$emit('input', { [this.startKey]: this.customRange.start, [this.endKey]: this.customRange.end })
    },
  },
}
</script>

<style lang="scss" scoped>
/* ── track ── */
.cal-mbar { margin-bottom: 10px; position: relative; }
.cal-mbar__track {
  display: flex; flex-wrap: wrap; gap: 6px; justify-content: center;
  background: #f1f5f9; border-radius: 12px; padding: 5px 6px;
}
.cal-mbar__btn {
  border: none; outline: none; cursor: pointer;
  background: transparent; color: #64748b;
  font-size: .78rem; font-weight: 600; line-height: 1;
  padding: 7px 13px; border-radius: 8px; white-space: nowrap; text-transform: capitalize;
  transition: all .18s ease;
}
.cal-mbar__btn:hover:not(.cal-mbar__btn--active) { background: rgba(5,150,105,.08); color: #059669; }
.cal-mbar__btn--current:not(.cal-mbar__btn--active) { background: rgba(5,150,105,.06); color: #059669; font-weight: 700; }
.cal-mbar__btn--active {
  background: linear-gradient(135deg, #059669, #34d399); color: #fff; font-weight: 700;
  box-shadow: 0 3px 10px rgba(5,150,105,.3);
}

/* ── selected range display ── */
.cal-mbar__range { display: flex; align-items: center; justify-content: center; margin-top: 8px; font-size: .8rem; font-weight: 600; color: #475569; }
.cal-mbar__range-sep { margin: 0 6px; opacity: .4; }

/* ── dropdown panel ── */
.cal-mdrop {
  position: absolute; top: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  z-index: 9999; width: 520px; max-width: 96vw;
  background: #fff; border-radius: 16px;
  border: 1px solid rgba(0,0,0,.07);
  box-shadow: 0 16px 60px rgba(0,0,0,.14);
  overflow: visible;
}
.cal-mdrop__header {
  display: flex; align-items: center; padding: 14px 18px;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-bottom: 1px solid #e2e8f0; border-radius: 16px 16px 0 0;
  font-size: .9rem; font-weight: 700; color: #334155; gap: 6px;
}
.cal-mdrop__header-icon { color: #059669; font-size: 1rem; }
.cal-mdrop__close { margin-left: auto; background: none; border: none; cursor: pointer; color: #94a3b8; padding: 2px 6px; border-radius: 6px; transition: background .15s, color .15s; }
.cal-mdrop__close:hover { background: rgba(0,0,0,.06); color: #475569; }
.cal-mdrop__body { padding: 20px 22px 10px; }
.cal-mdrop__footer { padding: 10px 22px 16px; display: flex; justify-content: flex-end; }
.cal-mdrop__apply {
  border: none; cursor: pointer; border-radius: 10px; padding: 9px 22px;
  background: linear-gradient(135deg, #059669, #34d399); color: #fff;
  font-size: .85rem; font-weight: 700;
  box-shadow: 0 4px 14px rgba(5,150,105,.35);
  transition: box-shadow .2s, transform .15s;
}
.cal-mdrop__apply:hover { box-shadow: 0 6px 20px rgba(5,150,105,.45); transform: translateY(-1px); }

/* ── transitions ── */
.cal-mbar-fade-enter-active, .cal-mbar-fade-leave-active { transition: opacity .25s, transform .25s; }
.cal-mbar-fade-enter, .cal-mbar-fade-leave-to { opacity: 0; transform: translateY(-4px); }
.cal-drop-enter-active, .cal-drop-leave-active { transition: opacity .2s, transform .2s; }
.cal-drop-enter, .cal-drop-leave-to { opacity: 0; transform: translateX(-50%) translateY(-8px); }
</style>

<style lang="scss">
/* dark */
.dark-layout .cal-mbar__track { background: #1e2a3e; }
.dark-layout .cal-mbar__btn { color: #94a3b8; }
.dark-layout .cal-mbar__btn:hover:not(.cal-mbar__btn--active) { background: rgba(5,150,105,.15); color: #6ee7b7; }
.dark-layout .cal-mbar__btn--current:not(.cal-mbar__btn--active) { background: rgba(5,150,105,.12); color: #6ee7b7; }
.dark-layout .cal-mbar__btn--active { background: linear-gradient(135deg, #047857, #059669); }
.dark-layout .cal-mbar__range { color: #b4b7bd; }
.dark-layout .cal-mdrop {
  background: #1e293b; border-color: rgba(255,255,255,.08);
  box-shadow: 0 16px 60px rgba(0,0,0,.5);
}
.dark-layout .cal-mdrop__header { background: linear-gradient(135deg, #0f172a, #1e293b); border-color: rgba(255,255,255,.08); color: #e2e8f0; }
.dark-layout .cal-mdrop__close { color: #64748b; }
.dark-layout .cal-mdrop__close:hover { background: rgba(255,255,255,.06); color: #94a3b8; }

/* month-picker inside dark */
.dark-layout .cal-mdrop .month-picker__container {
  background: #1e293b !important; border: 1px solid rgba(255,255,255,.08) !important;
  border-radius: 12px !important; box-shadow: 0 8px 30px rgba(0,0,0,.4) !important;
}
.dark-layout .cal-mdrop .month-picker__year { background: rgba(255,255,255,.04) !important; border-color: rgba(255,255,255,.06) !important; color: #e2e8f0 !important; }
.dark-layout .cal-mdrop .month-picker__year button { color: #94a3b8 !important; }
.dark-layout .cal-mdrop .month-picker__year input { background: rgba(255,255,255,.06) !important; border-color: rgba(255,255,255,.08) !important; color: #e2e8f0 !important; }
.dark-layout .cal-mdrop .month-picker__month { color: #94a3b8 !important; }
.dark-layout .cal-mdrop .month-picker__month:hover:not(.inactive) { background: rgba(255,255,255,.08) !important; color: #e2e8f0 !important; }
.dark-layout .cal-mdrop .month-picker__month.selected { background: rgba(5,150,105,.3) !important; color: #6ee7b7 !important; }

/* month-picker in cal-month-picker (body) */
.dark-layout .cal-mdrop .cal-month-picker__label { color: #94a3b8 !important; }
.dark-layout .cal-mdrop .month-picker-input-container .form-control {
  background: #1e2a3e !important; border-color: rgba(255,255,255,.1) !important; color: #e2e8f0 !important;
}
</style>
